import posthog from 'posthog-js';
import { ApiUserMain } from '@eencloud/eewc-components/src/service/api-types';
import { useUsersStore, useAccountStore } from '@/stores';
import useUserPreference from '../useUserPreference';
import { WEB_INTERFACE } from '@/constants';

export default function useIdentifyAndTrackUser() {
  const usersStore = useUsersStore();
  const accountStore = useAccountStore();
  const userPreference = useUserPreference();

  function isRunningInIframe() {
    try {
      // Check if window.self and window.top are different
      if (window.self !== window.top) {
        console.log('Running in an iframe');
        return true;
      }
      return false;
    } catch (e) {
      // If we get a security error, we're likely in a cross-origin iframe
      return true;
    }
  }

  async function shouldTrackUser() {
    // if url path has help-center-external return false
    if (window.location.pathname.includes('help-center-external')) {
      return false;
    }
    const res = await userPreference.getLastUsedApp();
    if (res?.value === WEB_INTERFACE.CLASSIC) {
      // if last used app is classic return false
      return false;
    }
    return true;
  }

  async function identifyAndTrackUser(user: ApiUserMain) {
    const trackUser = await shouldTrackUser();
    if (!user?.id || !trackUser) return;
    if (!usersStore.userContext) await usersStore.getUserContext();
    const accountDetails = await accountStore.fetchAccountWithCustomParam({
      include: 'resourceCounts',
    });
    const isInternalUser =
      user.email && (user.email.includes('@een.com') || user.email.includes('@eagleeyenetworks.com'));
    posthog.identify(user.id, {
      een_administrator: usersStore.userContext?.administrator ? true : false,
      een_account_id: usersStore.userContext?.accountId,
      een_parent_account_id: usersStore.userContext?.parentAccountId,
      een_cluster_id: usersStore.userContext?.clusterId,
      een_white_labeled: usersStore.userContext?.whiteLabeled ? true : false,
      een_account_type: usersStore.userContext?.accountType,
      een_edition: usersStore.userContext?.editionId,
      een_viewing_account_id: accountDetails?.id,
      een_viewing_account_type: accountDetails?.type,
    });
    // Get network information
    type ExtendedNetworkInformation = NetworkInformation & {
      mozConnection?: NetworkInformation;
      webkitConnection?: NetworkInformation;
    };
    const connection = (navigator as ExtendedNetworkInformation).connection ||
      (navigator as ExtendedNetworkInformation).mozConnection ||
      (navigator as ExtendedNetworkInformation).webkitConnection;

    posthog.register({
      een_user_experience_version: 'enhanced',
      een_internal_users: isInternalUser ? true : false,
      een_developer_iframe: isRunningInIframe(),
      een_administrator: usersStore.userContext?.administrator ? true : false,
      een_account_id: usersStore.userContext?.accountId,
      een_parent_account_id: usersStore.userContext?.parentAccountId,
      een_cluster_id: usersStore.userContext?.clusterId,
      een_white_labeled: usersStore.userContext?.whiteLabeled ? true : false,
      een_account_type: usersStore.userContext?.accountType,
      een_edition: usersStore.userContext?.editionId,
      een_resource_counts_cameras: accountDetails?.resourceCounts?.cameras,
      een_resource_counts_locations: accountDetails?.resourceCounts?.locations,
      een_resource_counts_bridges: accountDetails?.resourceCounts?.bridges,
      een_resource_counts_users: accountDetails?.resourceCounts?.users,
      een_resource_counts_layouts: accountDetails?.resourceCounts?.layouts,
      een_viewing_account_id: accountDetails?.id,
      een_viewing_account_type: accountDetails?.type,
      een_webapp_version: config.version,
      // Network details
      een_browser_network_type: connection?.type || '',
      een_browser_network_effective_type: connection?.effectiveType || '',
      een_browser_network_downlink: connection?.downlink || '',
      een_browser_network_rtt: connection?.rtt || '',
      een_browser_network_save_data: connection?.saveData || false,
      een_browser_user_agent: navigator.userAgent,
      een_browser_platform: navigator.platform,
      een_browser_online_status: navigator.onLine,
    });
    posthog.capture('user_opens_webapp');
  }

  function resetTracking() {
    posthog.reset();
  }

  return { identifyAndTrackUser, resetTracking };
}
